import { Injectable } from '@angular/core';
import { WorkItemListenerService } from 'common/workitems/workitem-listener.service';
import { Observable, Subject, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { PCacheable } from 'ngx-cacheable';
import { TriggerTypes } from '../agent-stat.config';
import { Trigger } from '../models/agent-stat.model';
import moment, {unitOfTime} from 'moment';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';

const REFRESH_TRIGGER_TIME_INTERVAL = 180000;

@Injectable()
export class TriggerService {
  private readonly _timer = timer(REFRESH_TRIGGER_TIME_INTERVAL, REFRESH_TRIGGER_TIME_INTERVAL);

  constructor(private _workItemListenerService: WorkItemListenerService) {
  }

  @PCacheable({
    maxCacheCount: 10
  })
  public addTriggerForSource(triggerType: TriggerTypes): Promise<Trigger> {
    const obs = this._createTrigger(triggerType);
    const refreshTargets = [];
    obs.subscribe(() => {
      const uniqueList = new Set(refreshTargets);
      uniqueList.forEach(target => target.refresh());
    });
    return Promise.resolve({
      obs,
      refreshTargets
    });
  }

  private _createTrigger(triggerType: TriggerTypes): Observable<unknown> {
    switch (triggerType) {
      case TriggerTypes.CHAT_ENDED_TRIGGER:
        return this._chatEndedTrigger();
      case TriggerTypes.DAILY_REFRESH:
        return this._refreshTriggerByPeriod('day');
      case TriggerTypes.WEEKLY_REFRESH:
        return this._refreshTriggerByPeriod('week');
      case TriggerTypes.CONVERSION_AMOUNT_TRIGGER:
      case TriggerTypes.CONVERSION_COUNT_TRIGGER:
      case TriggerTypes.SURVEY_NPS_TRIGGER:
      case TriggerTypes.SURVEY_KNOWLEDGE_TRIGGER:
      case TriggerTypes.SURVEY_PROFESSIONALISM_TRIGGER:
      case TriggerTypes.SURVEY_OVERALL_TRIGGER:
      case TriggerTypes.SURVEY_RESPONSIVENESS_TRIGGER:
        return this._refreshTriggerByInterval();
      default:
        break;
    }
  }

  private _chatEndedTrigger(): Observable<any> {
    return this._workItemListenerService.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.CHAT).pipe(
      filter((workItem: ChatItem) => {
        return !!workItem.Ended;
      })
    );
  }

  private _refreshTriggerByPeriod(period: unitOfTime.StartOf): Observable<any> {
    const newSubject = new Subject<void>();
    const nowInMs = moment().valueOf();
    const endOfDateInMs = moment().endOf(period).valueOf();
    const timeDiff = endOfDateInMs - nowInMs;

    setTimeout(() => {
      newSubject.next();
    }, timeDiff);

    return newSubject;
  }

  private _refreshTriggerByInterval(): Observable<unknown> {
    return this._timer;
  }
}
